/**
 * Users & Permissions Page - Admin only
 * Allows admins to manage users and configure command permissions
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Collapse,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Skeleton,
  Snackbar,
  TablePagination,
  Tooltip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  LockReset as PasswordIcon,
  Terminal as CommandIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  GroupOutlined as GroupIcon,
  PersonAdd as AddMemberIcon,
  PersonRemove as RemoveMemberIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { adminApi, UserInfo, InvitationCodeResponse } from '@/services/api';
import { getErrorMessage } from '@/utils/errors';
import type { UserGroup, UserGroupDetail } from '@/types';

// Command definitions with descriptions
const COMMAND_DEFINITIONS: Record<string, { description: string; category: 'all' | 'team_lead' | 'admin' }> = {
  help: { description: 'Show available commands and usage', category: 'all' },
  ask: { description: 'Ask a follow-up question in the thread', category: 'all' },
  status: { description: 'Show thread status and linked JIRA tickets', category: 'all' },
  docs: { description: 'Search team documentation', category: 'all' },
  summary: { description: 'Generate AI summary of thread discussion', category: 'all' },
  create: { description: 'Create a new JIRA ticket from thread', category: 'all' },
  assign: { description: 'Assign issue to a team expert', category: 'all' },
  link: { description: 'Link thread to existing JIRA ticket', category: 'all' },
  bad: { description: 'Flag bot response as incorrect or unhelpful', category: 'all' },
  reset: { description: 'Clear thread context and start fresh', category: 'all' },
  update: { description: 'Add status update to linked tickets', category: 'team_lead' },
  resolve: { description: 'Mark issue as resolved', category: 'team_lead' },
  reopen: { description: 'Reopen a resolved issue', category: 'team_lead' },
  escalate: { description: 'Escalate to responsible team', category: 'team_lead' },
  priority: { description: 'Change ticket priority (P0-P3)', category: 'team_lead' },
  broadcast: { description: 'Send message to team', category: 'team_lead' },
  config: { description: 'View/update team settings', category: 'admin' },
  addexpert: { description: 'Add expert to competency matrix', category: 'admin' },
  removeexpert: { description: 'Remove expert from competency matrix', category: 'admin' },
  enable: { description: 'Enable a command for the team', category: 'admin' },
  disable: { description: 'Disable a command for the team', category: 'admin' },
};


interface DefaultCommandPermission {
  command: string;
  description: string;
  allowed_roles: string[];
  category: 'all' | 'team_lead' | 'admin';
}

// ============================================================================
// Users Tab Component
// ============================================================================
function UsersTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'' | 'user' | 'admin' | 'super_admin'>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [newAdminStatus, setNewAdminStatus] = useState({ is_admin: false, is_super_admin: false });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', search, page, rowsPerPage, roleFilter, statusFilter],
    queryFn: () =>
      adminApi.listUsers({
        search: search || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }).then((res) => res.data),
    placeholderData: (previousData) => previousData,
  });

  // Client-side role filtering (API doesn't have role param)
  const filteredItems = (() => {
    if (!data?.items || !roleFilter) return data?.items;
    return data.items.filter((user) => {
      if (roleFilter === 'super_admin') return user.is_super_admin;
      if (roleFilter === 'admin') return user.is_admin && !user.is_super_admin;
      return !user.is_admin;
    });
  })();

  const handleClearFilters = () => {
    setRoleFilter('');
    setStatusFilter('');
    setSearch('');
    setPage(0);
  };

  const hasActiveFilters = roleFilter !== '' || statusFilter !== '';

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { is_active?: boolean } }) =>
      adminApi.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const updateAdminMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { is_admin: boolean; is_super_admin?: boolean } }) =>
      adminApi.updateUserAdmin(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setAdminDialogOpen(false);
      setSelectedUser(null);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      adminApi.resetUserPassword(userId, newPassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    },
  });

  const handleToggleActive = (user: UserInfo) => {
    updateUserMutation.mutate({
      userId: user.id,
      data: { is_active: !user.is_active },
    });
  };

  const handleOpenAdminDialog = (user: UserInfo) => {
    setSelectedUser(user);
    setNewAdminStatus({
      is_admin: user.is_admin,
      is_super_admin: user.is_super_admin,
    });
    setAdminDialogOpen(true);
  };

  const handleSaveAdminStatus = () => {
    if (selectedUser) {
      updateAdminMutation.mutate({
        userId: selectedUser.id,
        data: newAdminStatus,
      });
    }
  };

  const handleOpenPasswordDialog = (user: UserInfo) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleResetPassword = () => {
    if (selectedUser && newPassword.length >= 6) {
      resetPasswordMutation.mutate({
        userId: selectedUser.id,
        newPassword,
      });
    }
  };

  if (error) {
    return <Alert severity="error">Failed to load users. Please try again.</Alert>;
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Search users by email or name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            size="small"
          />
          <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
            <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)} color={filtersOpen || hasActiveFilters ? 'primary' : 'default'}>
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Filters (collapsible) */}
      <Collapse in={filtersOpen}>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value as '' | 'user' | 'admin' | 'super_admin');
                    setPage(0);
                  }}
                  label="Role"
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as '' | 'active' | 'inactive');
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters} disabled={!hasActiveFilters && !search}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role & Status</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(4)].map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : !filteredItems?.length ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No users found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems?.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {user.display_name || user.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {user.is_super_admin ? (
                        <Chip size="small" icon={<AdminIcon />} label="Super Admin" color="error" />
                      ) : user.is_admin ? (
                        <Chip size="small" icon={<AdminIcon />} label="Admin" color="primary" />
                      ) : (
                        <Chip size="small" icon={<PersonIcon />} label="User" variant="outlined" />
                      )}
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: user.is_active ? 'success.main' : 'text.disabled',
                        }}
                        title={user.is_active ? 'Active' : 'Inactive'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{new Date(user.created_at).toLocaleDateString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={user.is_active ? 'Deactivate user' : 'Activate user'}>
                      <IconButton size="small" onClick={() => handleToggleActive(user)} disabled={updateUserMutation.isPending}>
                        {user.is_active ? <BlockIcon /> : <ActiveIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset password">
                      <IconButton size="small" onClick={() => handleOpenPasswordDialog(user)}>
                        <PasswordIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage admin privileges">
                      <IconButton size="small" onClick={() => handleOpenAdminDialog(user)} color={user.is_admin ? 'primary' : 'default'}>
                        <AdminIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </TableContainer>

      {/* Admin Status Dialog */}
      <Dialog open={adminDialogOpen} onClose={() => setAdminDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Manage Admin Privileges</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>{selectedUser.email}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={newAdminStatus.is_admin}
                    onChange={(e) => setNewAdminStatus({
                      is_admin: e.target.checked,
                      is_super_admin: e.target.checked ? newAdminStatus.is_super_admin : false,
                    })}
                  />
                }
                label="Admin"
                sx={{ display: 'block', mt: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                Can approve teams and manage settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={newAdminStatus.is_super_admin}
                    onChange={(e) => setNewAdminStatus((prev) => ({ ...prev, is_super_admin: e.target.checked }))}
                    disabled={!newAdminStatus.is_admin}
                  />
                }
                label="Super Admin"
                sx={{ display: 'block', mt: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                Full access including user management
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAdminStatus} variant="contained" disabled={updateAdminMutation.isPending}>
            {updateAdminMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Reset password for: {selectedUser.email}
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mt: 2 }}
                helperText="Password must be at least 6 characters"
                error={newPassword.length > 0 && newPassword.length < 6}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={resetPasswordMutation.isPending || newPassword.length < 6}>
            {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Command Permissions Tab Component
// ============================================================================
function CommandPermissionsTab() {
  // Build the default permissions list from COMMAND_DEFINITIONS
  const defaultPermissions: DefaultCommandPermission[] = Object.entries(COMMAND_DEFINITIONS).map(([command, def]) => {
    let defaultRoles: string[];
    switch (def.category) {
      case 'all':
        defaultRoles = ['member', 'assignee', 'team_lead', 'admin'];
        break;
      case 'team_lead':
        defaultRoles = ['assignee', 'team_lead', 'admin'];
        break;
      case 'admin':
        defaultRoles = ['admin'];
        break;
    }
    return {
      command,
      description: def.description,
      allowed_roles: defaultRoles,
      category: def.category,
    };
  });

  const getRoleColor = (role: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (role) {
      case 'admin': return 'error';
      case 'team_lead': return 'warning';
      case 'assignee': return 'info';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: 'all' | 'team_lead' | 'admin') => {
    switch (category) {
      case 'all': return 'All Users';
      case 'team_lead': return 'Team Leads+';
      case 'admin': return 'Admins Only';
    }
  };

  const getCategoryColor = (category: 'all' | 'team_lead' | 'admin'): 'success' | 'warning' | 'error' => {
    switch (category) {
      case 'all': return 'success';
      case 'team_lead': return 'warning';
      case 'admin': return 'error';
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        These are the default command permissions applied to new teams. Each team can customize their own permissions in Team Settings.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Command</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Default Access</TableCell>
              <TableCell>Allowed Roles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {defaultPermissions.map((permission) => (
              <TableRow key={permission.command} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CommandIcon color="action" fontSize="small" />
                    <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                      !{permission.command}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {permission.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getCategoryLabel(permission.category)}
                    color={getCategoryColor(permission.category)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {permission.allowed_roles.map((role) => (
                      <Chip
                        key={role}
                        size="small"
                        label={role}
                        color={getRoleColor(role)}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ============================================================================
// Invite Codes Tab Component
// ============================================================================
function InviteCodesTab() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'available' | 'used'>('all');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [emailHint, setEmailHint] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [generatedCode, setGeneratedCode] = useState<InvitationCodeResponse | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invitation-codes', filter],
    queryFn: () => {
      const params: { is_used?: boolean } = {};
      if (filter === 'available') params.is_used = false;
      if (filter === 'used') params.is_used = true;
      return adminApi.listInvitationCodes(params).then((res) => res.data);
    },
  });

  const generateMutation = useMutation({
    mutationFn: (data?: { email_hint?: string; expires_in_days?: number }) =>
      adminApi.generateInvitationCode(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-codes'] });
      setGeneratedCode(response.data);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (codeId: string) => adminApi.deleteInvitationCode(codeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitation-codes'] });
    },
  });

  const handleGenerateCode = () => {
    const data: { email_hint?: string; expires_in_days?: number } = {};
    if (emailHint.trim()) data.email_hint = emailHint.trim();
    if (expiresInDays) data.expires_in_days = expiresInDays;
    generateMutation.mutate(Object.keys(data).length > 0 ? data : undefined);
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setEmailHint('');
    setExpiresInDays(null);
    setGeneratedCode(null);
  };

  const handleCopyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getCodeStatus = (code: InvitationCodeResponse): { label: string; color: 'success' | 'default' | 'error' } => {
    if (code.is_used) {
      return { label: 'Used', color: 'default' };
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return { label: 'Expired', color: 'error' };
    }
    return { label: 'Available', color: 'success' };
  };

  if (error) {
    return <Alert severity="error">Failed to load invitation codes. Please try again.</Alert>;
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => setFilter(e.target.value as 'all' | 'available' | 'used')}
          >
            <MenuItem value="all">All Codes</MenuItem>
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="used">Used</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setGenerateDialogOpen(true)}
        >
          Generate Code
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Email Hint</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No invitation codes found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.items.map((code) => {
                const status = getCodeStatus(code);
                return (
                  <TableRow key={code.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                          {code.code}
                        </Typography>
                        <Tooltip title={copiedCode === code.code ? 'Copied!' : 'Copy code'}>
                          <IconButton size="small" onClick={() => handleCopyCode(code.code)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {code.email_hint || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={
                            code.expires_at && !code.is_used
                              ? `${status.label} · Exp ${new Date(code.expires_at).toLocaleDateString()}`
                              : status.label
                          }
                          color={status.color}
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {code.is_used
                          ? `Used by ${code.used_by_email || 'unknown'}`
                          : `Created by ${code.created_by_email || 'unknown'}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(code.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {!code.is_used && (
                        <Tooltip title="Delete code">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteMutation.mutate(code.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate Code Dialog */}
      <Dialog open={generateDialogOpen} onClose={handleCloseGenerateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invitation Code</DialogTitle>
        <DialogContent>
          {generatedCode ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Your invitation code has been generated:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 2,
                  mb: 2,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" fontFamily="monospace" fontWeight={700}>
                  {generatedCode.code}
                </Typography>
                <Tooltip title={copiedCode === generatedCode.code ? 'Copied!' : 'Copy code'}>
                  <IconButton onClick={() => handleCopyCode(generatedCode.code)} color="primary">
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Share this code with the user. They will need it to register.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Email Hint (optional)"
                value={emailHint}
                onChange={(e) => setEmailHint(e.target.value)}
                placeholder="e.g., john.doe@company.com"
                helperText="Optional - helps you track who the code is for"
                sx={{ mb: 3 }}
              />
              <FormControl fullWidth>
                <InputLabel>Expiration</InputLabel>
                <Select
                  value={expiresInDays ?? ''}
                  label="Expiration"
                  onChange={(e) => setExpiresInDays(e.target.value === '' ? null : Number(e.target.value))}
                >
                  <MenuItem value="">Never expires</MenuItem>
                  <MenuItem value={7}>7 days</MenuItem>
                  <MenuItem value={30}>30 days</MenuItem>
                  <MenuItem value={90}>90 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenerateDialog}>
            {generatedCode ? 'Close' : 'Cancel'}
          </Button>
          {!generatedCode && (
            <Button
              onClick={handleGenerateCode}
              variant="contained"
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? 'Generating...' : 'Generate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Groups Tab Component
// ============================================================================
function GroupsTab() {
  const queryClient = useQueryClient();
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // List view state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail view state (null = show list)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Create/Edit dialog state
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<UserGroup | null>(null);

  // Add member dialog state
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  // Queries
  const groupsQuery = useQuery({
    queryKey: ['user-groups', search, page, rowsPerPage],
    queryFn: () =>
      adminApi.listGroups({
        search: search || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }).then((res) => res.data),
    placeholderData: (prev) => prev,
    enabled: !selectedGroupId,
  });

  const groupDetailQuery = useQuery({
    queryKey: ['user-group-detail', selectedGroupId],
    queryFn: () => adminApi.getGroup(selectedGroupId!).then((res) => res.data),
    enabled: !!selectedGroupId,
  });

  const usersQuery = useQuery({
    queryKey: ['users-for-group', memberSearch],
    queryFn: () =>
      adminApi.listUsers({
        search: memberSearch || undefined,
        limit: 50,
      }).then((res) => res.data),
    enabled: addMemberDialogOpen,
  });

  // Mutations
  const createGroupMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => adminApi.createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      handleCloseCreateEditDialog();
      setNotification({ message: 'Group created successfully', severity: 'success' });
    },
    onError: (error: Error) => {
      setNotification({ message: `Failed to create group: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: { name?: string; description?: string } }) =>
      adminApi.updateGroup(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      queryClient.invalidateQueries({ queryKey: ['user-group-detail'] });
      handleCloseCreateEditDialog();
      setNotification({ message: 'Group updated successfully', severity: 'success' });
    },
    onError: (error: Error) => {
      setNotification({ message: `Failed to update group: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => adminApi.deleteGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
      if (selectedGroupId) setSelectedGroupId(null);
      setNotification({ message: 'Group deleted successfully', severity: 'success' });
    },
    onError: (error: Error) => {
      setNotification({ message: `Failed to delete group: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      adminApi.addGroupMember(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-group-detail'] });
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      setNotification({ message: 'Member added to group', severity: 'success' });
    },
    onError: (error: Error) => {
      setNotification({ message: `Failed to add member: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      adminApi.removeGroupMember(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-group-detail'] });
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      setNotification({ message: 'Member removed from group', severity: 'success' });
    },
    onError: (error: Error) => {
      setNotification({ message: `Failed to remove member: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  // Handlers
  const handleOpenCreateDialog = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupDescription('');
    setCreateEditDialogOpen(true);
  };

  const handleOpenEditDialog = (group: UserGroup) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    setCreateEditDialogOpen(true);
  };

  const handleCloseCreateEditDialog = () => {
    setCreateEditDialogOpen(false);
    setEditingGroup(null);
    setGroupName('');
    setGroupDescription('');
  };

  const handleSaveGroup = () => {
    if (editingGroup) {
      updateGroupMutation.mutate({
        groupId: editingGroup.id,
        data: { name: groupName, description: groupDescription || undefined },
      });
    } else {
      createGroupMutation.mutate({
        name: groupName,
        description: groupDescription || undefined,
      });
    }
  };

  // Detail view
  if (selectedGroupId) {
    const detail = groupDetailQuery.data as UserGroupDetail | undefined;
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => setSelectedGroupId(null)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6">{detail?.name || 'Loading...'}</Typography>
          {detail?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {detail.description}
            </Typography>
          )}
        </Box>

        <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {detail?.members.length ?? 0} member{(detail?.members.length ?? 0) !== 1 ? 's' : ''}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddMemberIcon />}
            onClick={() => { setMemberSearch(''); setAddMemberDialogOpen(true); }}
          >
            Add Member
          </Button>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupDetailQuery.isLoading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(3)].map((_, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !detail?.members.length ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>No members yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                detail.members.map((member) => (
                  <TableRow key={member.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {member.display_name || member.email.split('@')[0]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{member.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(member.added_at).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from group">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeMemberMutation.mutate({ groupId: selectedGroupId, userId: member.user_id })}
                          disabled={removeMemberMutation.isPending}
                        >
                          <RemoveMemberIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Member Dialog */}
        <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Member to Group</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              placeholder="Search users by email or name..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ mt: 1, mb: 2 }}
            />
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {usersQuery.data?.items
                ?.filter((u) => !detail?.members.some((m) => m.user_id === u.id))
                .map((user) => (
                  <Box
                    key={user.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {user.display_name || user.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => addMemberMutation.mutate({ groupId: selectedGroupId, userId: user.id })}
                      disabled={addMemberMutation.isPending}
                    >
                      Add
                    </Button>
                  </Box>
                ))}
              {usersQuery.data?.items?.filter((u) => !detail?.members.some((m) => m.user_id === u.id)).length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {memberSearch ? 'No matching users found' : 'All users are already in this group'}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddMemberDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // List view
  return (
    <Box>
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search groups by name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Create Group
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupsQuery.isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(5)].map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : !groupsQuery.data?.items.length ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No groups found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              groupsQuery.data.items.map((group) => (
                <TableRow
                  key={group.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedGroupId(group.id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon color="action" fontSize="small" />
                      <Typography variant="body2" fontWeight={500}>{group.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {group.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={group.member_count} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{new Date(group.created_at).toLocaleDateString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit group">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(group); }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete group">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); setGroupToDelete(group); setDeleteConfirmOpen(true); }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {groupsQuery.data && (
          <TablePagination
            component="div"
            count={groupsQuery.data.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </TableContainer>

      {/* Create/Edit Group Dialog */}
      <Dialog open={createEditDialogOpen} onClose={handleCloseCreateEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveGroup}
            variant="contained"
            disabled={!groupName.trim() || createGroupMutation.isPending || updateGroupMutation.isPending}
          >
            {(createGroupMutation.isPending || updateGroupMutation.isPending) ? 'Saving...' : editingGroup ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group &quot;{groupToDelete?.name}&quot;?
            All member associations will be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => groupToDelete && deleteGroupMutation.mutate(groupToDelete.id)}
            color="error"
            variant="contained"
            disabled={deleteGroupMutation.isPending}
          >
            {deleteGroupMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================
export default function UserManagementPage() {
  const [mainTab, setMainTab] = useState(0);

  return (
    <Box>

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Users" />
          <Tab label="Groups" />
          <Tab label="Command Permissions" />
          <Tab label="Invite Codes" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {mainTab === 0 && <UsersTab />}
      {mainTab === 1 && <GroupsTab />}
      {mainTab === 2 && <CommandPermissionsTab />}
      {mainTab === 3 && <InviteCodesTab />}
    </Box>
  );
}
